import React, { useState, useMemo, useEffect } from "react";
import { CustomerViewEditViewFullInfoProps } from "./customer-view-edit-view-full-info.props";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import { MARITAL_STATUS_OPTIONS } from "../../../../../../constants/leadDropdown";
import leadInfoIcon from "../../../../../../assets/icons/leadInfo.svg";
import {
  TextInputComponent,
  SelectInputComponent,
  DescriptionInputComponent,
  RevenueInputComponent,
} from "../../../../../../common/components";
import { updateCustomerDetails } from "../../../../../../api/customer";
import "./customer-view-edit-view-full-info.scss";
import { validationSchema } from "./customer-view-edit-view-full-info.validate";
import isEqual from "lodash/isEqual";
import CountryCodeWithPhoneNumber from "../../../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { isOnlyCountryCode } from "../../../../../../utils/utils";
import { useLogin } from "../../../../../../common/hooks/useUser";
import { Assistant } from "@mui/icons-material";
import { PREFERRED_CONTACT_MODE_OPTIONS } from "../../../../voice-create-customer/create-customer/create-customer-helper";
import dayjs from "dayjs";

const CustomerViewEditViewFullInfo: React.FC<
  CustomerViewEditViewFullInfoProps
> = (props: any) => {
  const {
    customerDetails,
    setViewFullInfoOpen,
    selectedCustomerId,
    handleCancelUpdate,
    setAlertType,
    setAlertMessage,
    setAlertOpen,
    setLoading,
    uploadedImageFile,
    removeImage,
    resetImageUpload,
    handleFormSubmitError,
    isMobile,
  } = props;
  const formikRef: any = React.useRef(null);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const [hasAttemptedSave, setHasAttemptedSave] = useState<boolean>(false);
  const { tenantDetails } = useLogin();

  const checkDisableField = () => {
    return tenantDetails?.tenantType === "SuperTenant" &&
      customerDetails?.assigned_to &&
      !(customerDetails?.lead_status === "Closed-Lost")
      ? true
      : false;
  };
  const sanitize = (value: any): string => {
    return value === null || value === undefined || value === "null"
      ? ""
      : value;
  };

  const initialValues = useMemo(
    () => ({
      title: sanitize(customerDetails?.title),
      firstName: sanitize(customerDetails?.first_name),
      lastName: sanitize(customerDetails?.last_name),
      email: sanitize(customerDetails?.primary_email_address),
      secondaryEmail: sanitize(customerDetails?.secondary_email_address),
      fax: sanitize(customerDetails?.fax_number),
      dob: customerDetails?.dob
        ? dayjs.utc(customerDetails?.dob).local()
        : null,

      description: sanitize(customerDetails?.description),

      customerOwner:
        sanitize(String(customerDetails?.customerOwner?.id)) ?? null,
      selectedCustomerSource: customerDetails?.lead_source ?? null,
      selectedCustomerStatus: customerDetails?.lead_status ?? null,

      primaryPhoneNumber:
        sanitize(customerDetails.primary_phone_number) || null,
      secondaryPhoneNumber:
        sanitize(customerDetails.secondary_phone_number) || null,
      middleName: customerDetails?.middle_name || null,
      leadValue: customerDetails?.lead_value || null,
      maritalStatus: customerDetails?.marital_status || null,
      assistant: customerDetails?.assistant || null,
      assistantPhoneNumber: customerDetails?.assistant_phone_number || null,
      company: customerDetails?.company || null,
      selectedpreferredContactMode:
        customerDetails?.preferred_contact_mode ?? null,
      mailingStreetLine1: sanitize(
        customerDetails?.mailingAddress?.street_line_1
      ),
      mailingStreetLine2: sanitize(
        customerDetails?.mailingAddress?.street_line_2
      ),
      mailingState: sanitize(customerDetails?.mailingAddress?.state),
      mailingCity: sanitize(customerDetails?.mailingAddress?.city),
      mailingCountry: sanitize(customerDetails?.mailingAddress?.country),
      mailingZipcode: sanitize(customerDetails?.mailingAddress?.zipcode),
      // Permanent Address
      permanentStreetLine1: sanitize(
        customerDetails?.permanentAddress?.street_line_1
      ),
      permanentStreetLine2: sanitize(
        customerDetails?.permanentAddress?.street_line_2
      ),
      permanentState: sanitize(customerDetails?.permanentAddress?.state),
      permanentCity: sanitize(customerDetails?.permanentAddress?.city),
      permanentCountry: sanitize(customerDetails?.permanentAddress?.country),
      permanentZipcode: sanitize(customerDetails?.permanentAddress?.zipcode),
    }),
    [customerDetails]
  );
  const handleCancel = () => {
    resetImageUpload?.();
    setViewFullInfoOpen(false);
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const payload = {
        title: values.title || null,
        firstName: values.firstName,
        lastName: values.lastName || null,
        primaryEmail: values.email,
        dob: values.dob ? new Date(values.dob).toISOString() : null,
        secondaryEmail: values.secondaryEmail || null,
        ...(isOnlyCountryCode(values.primaryPhoneNumber)
          ? {}
          : { primaryPhoneNumber: values.primaryPhoneNumber || null }),
        ...(isOnlyCountryCode(values.secondaryPhoneNumber)
          ? {}
          : { secondaryPhoneNumber: values.secondaryPhoneNumber || null }),
        mailingAddress: {
          streetLine1: values.mailingStreetLine1 || null,
          streetLine2: values.mailingStreetLine2 || null,
          city: values.mailingCity || null,
          state: values.mailingState || null,
          country: values.mailingCountry || null,
          zipcode: values.mailingZipcode || null,
        },
        permanentAddress: {
          streetLine1: values.permanentStreetLine1 || null,
          streetLine2: values.permanentStreetLine2 || null,
          city: values.permanentCity || null,
          state: values.permanentState || null,
          country: values.permanentCountry || null,
          zipcode: values.permanentZipcode || null,
        },
        leadSource: values.selectedCustomerSource || null,
        faxNumber: isOnlyCountryCode(values.fax) ? null : values.fax || null,
        description: values.description || null,
        middleName: values.middleName || null,
        page: "second",
        company: values?.company || null,
        preferredContactMode: values?.selectedpreferredContactMode || null,
        assistant: values.assistant || null,
        ...(isOnlyCountryCode(values.assistantPhoneNumber)
          ? {}
          : { assistantPhoneNumber: values.assistantPhoneNumber || null }),
        customerOwnerId: values.customerOwner || null,
      };

      Object.entries(payload).forEach(([key, value]) => {
        if (
          key !== "mailingAddress" &&
          key !== "permanentAddress" &&
          value !== null &&
          value !== undefined
        ) {
          formData.append(key, value);
        }
      });

      const appendAddressField = (
        form: FormData,
        prefix: string,
        key: string,
        value: any
      ) => {
        if (value !== null && value !== undefined && value !== "") {
          form.append(`${prefix}[${key}]`, value);
        }
      };

      appendAddressField(
        formData,
        "mailingAddress",
        "streetLine1",
        values.mailingStreetLine1
      );
      appendAddressField(
        formData,
        "mailingAddress",
        "streetLine2",
        values.mailingStreetLine2
      );
      appendAddressField(
        formData,
        "mailingAddress",
        "city",
        values.mailingCity
      );
      appendAddressField(
        formData,
        "mailingAddress",
        "state",
        values.mailingState
      );
      appendAddressField(
        formData,
        "mailingAddress",
        "country",
        values.mailingCountry
      );
      appendAddressField(
        formData,
        "mailingAddress",
        "zipcode",
        values.mailingZipcode
      );

      appendAddressField(
        formData,
        "permanentAddress",
        "streetLine1",
        values.permanentStreetLine1
      );
      appendAddressField(
        formData,
        "permanentAddress",
        "streetLine2",
        values.permanentStreetLine2
      );
      appendAddressField(
        formData,
        "permanentAddress",
        "city",
        values.permanentCity
      );
      appendAddressField(
        formData,
        "permanentAddress",
        "state",
        values.permanentState
      );
      appendAddressField(
        formData,
        "permanentAddress",
        "country",
        values.permanentCountry
      );
      appendAddressField(
        formData,
        "permanentAddress",
        "zipcode",
        values.permanentZipcode
      );

      if (values.imageUrl instanceof File) {
        formData.append("customerImg", values.imageUrl);
      }
      const response = await updateCustomerDetails(
        selectedCustomerId,
        formData
      );

      if (response.data.success) {
        if (props.onCustomerNameUpdate) {
          props.onCustomerNameUpdate(
            `${values.firstName} ${values.lastName}`.trim()
          );
        }
        setAlertMessage("Customer details updated successfully");
        setAlertType("success");
        setAlertOpen(true);
        handleCancelUpdate();
        setLoading(false);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formikRef) {
      formikRef?.current?.validateForm();
      formikRef?.current?.validateField();
      setTimeout(() => {
        if (formikRef?.current?.isValid === false) {
          formikRef?.current?.handleSubmit();
        }
      }, 500);
    }
  }, []);

  const checkAddDetails = (errors: any) => {
    Object.keys(errors)?.forEach((key) => {
      const isFromDetailsTab = [""]?.includes(key);
      if (isFromDetailsTab) {
        handleFormSubmitError("details");
      }
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(true)}
      onSubmit={(values) => handleSave(values)}
      validateOnChange={true}
      validateOnMount={true}
      validateOnBlur={true}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        setFieldTouched,
        touched,
        errors,
      }) => {
        const isFormDirty = !isEqual(values, initialValues);

        return (
          <Form>
            <div className="customer-list-view-details-view-box-full-info">
              {!isMobile && (
                <div className="customer-list-view-details-view-box-full-info-header">
                  <div>Additional Details</div>
                  <div>
                    {isViewOpen ? (
                      <i
                        className="fa fa-angle-down"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                        aria-hidden="true"
                      />
                    ) : (
                      <i
                        className="fa fa-angle-up"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </div>
              )}
              {isViewOpen ? (
                <>
                  <div className="customer-list-view-details-view-box-full-info-header-text">
                    Customer Information
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-data">
                    <div className="customer-list-view-details-view-box-full-info-data-section">
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Secondary Phone Number
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub secondary-error">
                          <CountryCodeWithPhoneNumber
                            fieldName="secondaryPhoneNumber"
                            placeholder={
                              values?.secondaryPhoneNumber
                                ? ""
                                : "Secondary phone number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any
                            ) => {
                              const countryCode = country?.dialCode;
                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object"
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("secondaryPhoneNumber", null);
                              } else {
                                setFieldValue("secondaryPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("secondaryPhoneNumber", true);
                            }}
                            value={values?.secondaryPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                            disabled={checkDisableField()}
                            // countryCodeEditable
                          />{" "}
                            {hasAttemptedSave &&
                              typeof errors.secondaryPhoneNumber === "string" && (
                              <div className="error-message">
                                  {errors.secondaryPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Fax
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <CountryCodeWithPhoneNumber
                            fieldName="fax"
                            placeholder={values?.fax ? "" : "Enter Fax Number"}
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any
                            ) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object"
                                );
                                return;
                              }
                              if (`+${countryCode}` == two) {
                                setFieldValue("fax", "");
                              } else {
                                setFieldValue("fax", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("fax", true);
                            }}
                            value={values?.fax || "+1"}
                            defaultCountry="us"
                            optional={true}
                            disabled={checkDisableField()}
                            // countryCodeEditable
                          />
                          {hasAttemptedSave && typeof errors.fax === "string" && (
                            <div className="error-message">{errors.fax}</div>
                          )}
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Assistant
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="assistant"
                            value={values.assistant}
                            placeholder="Enter Assisstant"
                            optional={false}
                            disabled={false}
                            onChange={(e: any) =>
                              setFieldValue("assistant", e?.target?.value)
                            }
                            maxlength="50"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="customer-list-view-details-view-box-full-info-data-section">
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Secondary Email
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="email"
                            fieldName="secondaryEmail"
                            placeholder="Enter Secondary Email"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.secondaryEmail}
                            onChange={(e: any) =>
                              setFieldValue("secondaryEmail", e?.target?.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Company
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="company"
                            value={values.company}
                            placeholder="Enter Company"
                            optional={false}
                            disabled={checkDisableField()}
                            onChange={(e: any) =>
                              setFieldValue("company", e?.target?.value)
                            }
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Assistant Phone Number
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub secondary-error">
                          <CountryCodeWithPhoneNumber
                            fieldName="assistantPhoneNumber"
                            placeholder={
                              values?.assistantPhoneNumber
                                ? ""
                                : "Assistant phone number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any
                            ) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object"
                                );
                                return;
                              }
                              if (`+${countryCode}` == two) {
                                setFieldValue("assistantPhoneNumber", null);
                              } else {
                                if (two?.includes(`+${countryCode}`)) {
                                  setFieldValue("assistantPhoneNumber", two);
                                } else {
                                  setFieldValue(
                                    "assistantPhoneNumber",
                                    countryCode
                                  );
                                }
                              }
                            }}
                            onKeyDown={(value: any) => {
                              if (
                                value.location == 0 &&
                                value.key == "Backspace"
                              ) {
                                setFieldValue(
                                  "assistantPhoneNumber",
                                  values?.assistantPhoneNumber?.substring(
                                    0,
                                    values?.assistantPhoneNumber?.length
                                  )
                                );
                              }
                              if (
                                value.location == 0 &&
                                value.key == "Delete"
                              ) {
                                setFieldValue(
                                  "assistantPhoneNumber",
                                  values?.assistantPhoneNumber?.substring(
                                    0,
                                    values?.assistantPhoneNumber?.length
                                  )
                                );
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("assistantPhoneNumber", true);
                            }}
                            value={values?.assistantPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                          />{" "}
                            {hasAttemptedSave &&
                              typeof errors.assistantPhoneNumber === "string" && (
                              <div className="error-message">
                                  {errors.assistantPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Preferred Contact Mode
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="preferredContactMode"
                            options={PREFERRED_CONTACT_MODE_OPTIONS}
                            onChange={(e: any) =>
                              setFieldValue("selectedpreferredContactMode", e)
                            }
                            value={values?.selectedpreferredContactMode}
                            disabled={checkDisableField()}
                            placeholder="Select Preferred Contact Mode"
                            optional={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-header-text">
                    Description
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-description-box">
                    <DescriptionInputComponent
                      fieldName="description"
                      placeholder="Add Description"
                      optional={false}
                      disabled={checkDisableField()}
                      value={values.description}
                      onChange={(e: any) =>
                        setFieldValue("description", e?.target?.value)
                      }
                      maxlength={150}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
            <Box className="footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasAttemptedSave(true);
                  checkAddDetails(errors);
                  handleSubmit();
                }}
                className="cancel-button next-button-wrapper"
                disabled={!(isFormDirty || uploadedImageFile || removeImage)}
              >
                Save
              </button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CustomerViewEditViewFullInfo;
